import React from 'react';
import fetch from 'isomorphic-unfetch';
import { buildDataUrl } from '@helper/api';
import PropTypes from 'prop-types';
import TemplateDefault from '@components/templates/default/default';
import { appSetPageData } from '@redux/actions/app';
import { wrapper } from '@redux/store';
import EducationStage from '@components/organisms/education/stage/educationStage';
import EducationNavBg from '@components/atoms/education/nav-bg/educationNavBg';
import EducationCategoryTeaser from '@components/organisms/education/category-teaser/educationCategoryTeaser';
import ContentBlocks from '@components/organisms/content-blocks/contentBlocks.withRedux';
import Pagination from '@components/organisms/pagination/pagination';
import Container from '@components/layout/container/container';
import EducationQuickFacts from '@components/organisms/education/quick-facts/educationQuickFacts';
import EducationTabletProcess from '@components/organisms/education/tablet-process/educationTabletProcess';
import EducationCollectionBar from '@components/organisms/education/collection-bar/educationCollectionBar';

export const getServerSideProps = wrapper.getServerSideProps(
  store =>
    async ({ params, locale, query }) => {
      const { slug } = params;
      const { token, collection } = query;

      const localizedSlug = locale === 'de' ? 'bildung' : 'education';

      let data = null;
      let collectionData = null;
      // const dev = process.env.NODE_ENV !== 'production';
      // const server = dev ? 'http://localhost:3000' : 'https://futurium.de';
      const uri = buildDataUrl(
        `${locale || 'de'}/${process.env.NEXT_PUBLIC_JSON_API_PATH}/${localizedSlug}/detail/${slug}${
          token ? `?token=${token}` : ''
        }`
      );
      try {
        const response = await fetch(uri, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            return {
              notFound: true
            };
          }
          throw new Error(`Failed to fetch data: ${response.status}`);
        }

        data = await response.json();

        if (collection !== null) {
          const collresponse = await fetch(
            `${buildDataUrl(
              `${locale || 'de'}/${process.env.NEXT_PUBLIC_JSON_API_PATH}/${localizedSlug}/${collection}${
                token ? `?token=${token}` : ''
              }`
            )}`,
            {
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );

          if (collresponse.ok) {
            collectionData = await collresponse.json();
          }
        }

        await store.dispatch(appSetPageData(data));

        return {
          props: {
            data,
            collectionData
          }
        };
      } catch (error) {
        console.error('Error fetching data:', error);
        return {
          props: {
            data,
            collectionData,
            error: error.message
          }
        };
      }
    }
);

function DetailPage({ collectionData = null, data }) {
  const educationLessonSlugs =
    collectionData && collectionData.educationLessons
      ? collectionData.educationLessons.map(
          lesson => `${lesson.uri}?collection=${collectionData.slug}`
        )
      : null;
  const currentPageIndex = educationLessonSlugs
    ? educationLessonSlugs.findIndex(link => link.includes(data.slug))
    : null;

  return (
    <TemplateDefault invertedHeader={data.educationTopics?.length > 1}>
      <EducationNavBg />
      {collectionData && collectionData.educationLessons && (
        <EducationCollectionBar link={collectionData.uri}>
          <Pagination
            noPadding
            align="left"
            customLink={educationLessonSlugs}
            currentPage={currentPageIndex + 1}
            totalPages={collectionData.educationLessons.length}
          />
        </EducationCollectionBar>
      )}
      <EducationStage />
      <EducationQuickFacts />
      {data?.type === 'workshop' ? <EducationTabletProcess /> : <ContentBlocks noShareLinks />}
      {collectionData && collectionData.educationLessons && (
        <Container large>
          <Pagination
            customLink={educationLessonSlugs}
            currentPage={currentPageIndex + 1}
            totalPages={collectionData.educationLessons.length}
          />
        </Container>
      )}
      <EducationCategoryTeaser />
    </TemplateDefault>
  );
}

DetailPage.propTypes = {
  collectionData: PropTypes.shape({
    uri: PropTypes.string,
    slug: PropTypes.string,
    educationLessons: PropTypes.arrayOf(
      PropTypes.shape({
        slug: PropTypes.string,
        uri: PropTypes.string
      })
    )
  }),
  data: PropTypes.shape({
    educationTopics: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired
      })
    ).isRequired,
    slug: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired
  }).isRequired
};

export default DetailPage;
